import urlLocationHelper from '@/scripts/helpers/url-location-helper';
import { HarmonyPromise, Nullable } from '@/scripts/models/utility-types';
import { VideoCollectionVideo } from '@/scripts/models/VideoCollectionVideo';
import { Model, Request } from '@viddsee/server-api-client';

import { IdentifierDefinition } from '../../identifierDefinitions';
import { BaseCollectionImplementationIdentifierKVMap, BaseCollectionImplementationIdentifierValue, makeIdentifierKVMap, makeIdentifierValues, UrlParams } from './helpers';

export interface CollectionImplementationFeatchers {
  info: () => HarmonyPromise<Nullable<Model.VideoCollection.Info>>;
  videoIndex: (params?: Request.Videos.SingleVideoUrlPath) => HarmonyPromise<number> ;
  videos: (params?: Request.Pagination.Options) => HarmonyPromise<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>>;
}
export interface CollectionImplementationDataOptions {
  implementation: CollectionImplementation;
  publicUrl?: string;
  author?: any;
  fetchers: CollectionImplementationFeatchers;
  transformers?: Record<string, Function>;
}

export class CollectionImplementationData {
  implementation: CollectionImplementation;

  publicUrl: string;

  author: any | null;

  fetchers: Nullable<CollectionImplementationFeatchers>;

  transformers: {
    video: (video: Model.Video.Info) => VideoCollectionVideo;
    [name: string]: undefined | ((...x: any[]) => any);
  };

  constructor({
    implementation,
    publicUrl,
    author,
    fetchers,
    transformers,
  }: CollectionImplementationDataOptions) {
    this.implementation = implementation;
    this.publicUrl = publicUrl || implementation.collectionPublicUrl;
    this.author = author || null;

    this.fetchers = fetchers || null;

    this.transformers = {
      video: (video: Model.Video.Info): VideoCollectionVideo => new VideoCollectionVideo(video, implementation.identifierValues),
      ...transformers,
    };
  }
}

export interface CollectionImplementationOptions {
  video?: Model.Video.Info;
  params?: UrlParams;
  identifierDefinitions?: IdentifierDefinition[];
}
export class CollectionImplementation {
  video?: Model.Video.Info;

  params: UrlParams;

  identifierKVMap: BaseCollectionImplementationIdentifierKVMap;

  identifierValues: Required<BaseCollectionImplementationIdentifierValue>[];

  constructor({ video, params, identifierDefinitions }: CollectionImplementationOptions) {
    this.video = video;
    this.params = params || this.getParams();
    this.identifierKVMap = makeIdentifierKVMap(identifierDefinitions || [], this.params);
    this.identifierValues = makeIdentifierValues(this.identifierKVMap);
  }

  getParams(): UrlParams {
    const params = (urlLocationHelper.query.parse() || {}) as Record<string, string | undefined>;

    // A hotfix for immediate backward compatibility
    if ('list' in params && params.list) {
      const [path] = params.list.split('?');
      const parts = path.split('/');

      parts.reduce((acc, val, index, arr) => {
        if (index % 2 === 1) return acc; // process every even iteration 0, 2, 4, ...

        const singularizedKey = val.replace(/s$/, '');
        acc[singularizedKey] = arr[index + 1];
        return acc;
      }, params);
    }

    return params;
  }

  get collectionPublicUrl(): string {
    const collectionPublicPaths = this.identifierValues.map(({ value }) => value);
    return `/${this.identifierValues[0].ids.publicId}/${collectionPublicPaths.join('/')}`;
  }

  get collectionApiRequestPath(): string {
    const apiRequestPathString = this.identifierValues
      .map(({ ids, value }) => [ids.apiId, value].join('/'))
      .join('/')
    ;
    return `/${apiRequestPathString}`;
  }

  /**
   * @returns boolean - return a boolean deciding if this implementation should be executed
   */
  test(): boolean {
    return false;
  }

  /**
   * @returns CollectionImplementationData - Argument to be resolved by this Promise. Can also be a Promise or a thenable to resolve.
   */
  provide(): HarmonyPromise<Nullable<CollectionImplementationData>> {
    return null;
  }
}
